package com.kinoshazam.screens.main.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.requiredHeight
import androidx.compose.foundation.layout.requiredSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.coerceAtLeast
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.max
import androidx.compose.ui.unit.times
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.right
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.roundToInt

@Composable
fun <T> CarouselRow(
    items: List<T>,
    modifier: Modifier = Modifier,
    itemWidth: Dp,
    itemHeight: Dp,
    itemSpacing: Dp = 16.dp,
    edgePadding: Dp = 24.dp,
    snap: Boolean = true,
    pageByVisible: Boolean = true,
    onItemClick: (index: Int, item: T) -> Unit = { _, _ -> },
    itemContent: @Composable (index: Int, item: T, isFocused: Boolean) -> Unit,
) {
    if (items.isEmpty()) return

    val density = LocalDensity.current
    var containerWidthDp by remember { mutableStateOf(0.dp) }
    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()

    // измеряем доступную ширину
    BoxWithConstraints(
        modifier
            .fillMaxWidth()
            .onSizeChanged { containerWidthDp = with(density) { it.width.toDp() } }
            .focusGroup() // важное для TV — запираем фокус внутри карусели
    ) {
        val itemFullWidthDp = itemWidth + itemSpacing
        val availableForItems = (containerWidthDp - edgePadding * 2).coerceAtLeast(1.dp)
        val perPage = max(
            1,
            floor(availableForItems / itemFullWidthDp).toInt()
        )
        val pageSize = if (pageByVisible) perPage else 1

        // --- SNAP после пользовательского скролла/флинга
        if (snap) {
            LaunchedEffect(listState.isScrollInProgress, itemFullWidthDp) {
                if (!listState.isScrollInProgress) {
                    val px = with(density) { itemFullWidthDp.roundToPx() }
                    val absOffsetPx =
                        listState.firstVisibleItemIndex * px + listState.firstVisibleItemScrollOffset
                    val nearestIndex = (absOffsetPx.toFloat() / px).roundToInt()
                        .coerceIn(0, items.lastIndex)
                    listState.animateScrollToItem(nearestIndex)
                }
            }
        }

        // --- слой с кнопками и LazyRow
        Row(Modifier.fillMaxWidth()) {

            // Кнопка «влево»
            val canLeft by remember {
                derivedStateOf { listState.firstVisibleItemIndex > 0 || listState.firstVisibleItemScrollOffset > 0 }
            }
            ArrowButton(
                enabled = canLeft,
                alignment = Alignment.CenterStart,
                onClick = {
                    val target = (listState.firstVisibleItemIndex - pageSize).coerceAtLeast(0)
                    // выравниваем к началу айтема
                    scope.launch { listState.animateScrollToItem(target) }
                }
            )

            // --- сама лента
            LazyRow(
                state = listState,
                contentPadding = PaddingValues(horizontal = edgePadding),
                horizontalArrangement = Arrangement.spacedBy(itemSpacing),
                modifier = Modifier
                    .fillMaxWidth()
                    .onPreviewKeyEvent { e ->
                        // Навигация стрелками / DPAD
                        if (e.type == KeyEventType.KeyUp) when (e.key) {
                            Key.DirectionRight -> {
                                val target =
                                    (listState.firstVisibleItemIndex + 1).coerceAtMost(items.lastIndex)
                                scope.launch { listState.animateScrollToItem(target) }
                                true
                            }

                            Key.DirectionLeft -> {
                                val target = (listState.firstVisibleItemIndex - 1).coerceAtLeast(0)
                                scope.launch { listState.animateScrollToItem(target) }
                                true
                            }

                            else -> false
                        } else false
                    }
            ) {
                itemsIndexed(items) { index, item ->
                    // фокус на карточке (TV)
                    val fr = remember { FocusRequester() }
                    var focused by remember { mutableStateOf(false) }

                    Box(
                        Modifier
                            .size(itemWidth, itemHeight)
                            .focusRequester(fr)
                            .onFocusChanged { focused = it.isFocused }
                            .focusable()
                            .pointerInput(Unit) {
                                detectTapGestures { onItemClick(index, item) }
                            }
                    ) {
                        itemContent(index, item, focused)
                    }
                }
            }

            // Кнопка «вправо»
            val canRight by remember {
                derivedStateOf {
                    val lastIndex = items.lastIndex
                    val lastVisible = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
                    lastVisible < lastIndex
                }
            }
            Box(modifier = Modifier.weight(1f)) {
                ArrowButton(
                    enabled = canRight,
                    alignment = Alignment.CenterEnd,
                    onClick = {
                        val target =
                            (listState.firstVisibleItemIndex + pageSize).coerceAtMost(items.lastIndex)
                        scope.launch { listState.animateScrollToItem(target) }
                    }
                )
            }
        }
    }
}

@Composable
private fun ArrowButton(
    enabled: Boolean,
    alignment: Alignment,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
//            .fillMaxHeight()
            .width(56.dp)
            .pointerInput(enabled) {
                if (enabled) detectTapGestures { onClick() }
            }
            .alpha(if (enabled) 1f else 0.35f),
        contentAlignment = alignment
    ) {
        Box(
            Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.12f))
        )
        // тут можно поставить иконку «<» / «>»
    }
}

@Composable
fun <T> LeftAlignedCarouselRow(
    items: List<T>,
    modifier: Modifier = Modifier,
    itemWidth: Dp,
    itemHeight: Dp,
    itemSpacing: Dp = 16.dp,
    leftPadding: Dp = 24.dp,    // фиксируем левый край
    minRightPadding: Dp = 24.dp,
    pageByWidth: Boolean = true, // листать страницами целиком; false — по 1 айтему
    onItemClick: (index: Int, item: T) -> Unit = { _, _ -> },
    itemContent: @Composable (index: Int, item: T, isFocused: Boolean) -> Unit,
) {
    if (items.isEmpty()) return

    val state = rememberLazyListState()
    val scope = rememberCoroutineScope()
    val density = LocalDensity.current
    var containerW by remember { mutableStateOf(0.dp) }

    BoxWithConstraints(
        modifier
            .fillMaxWidth()
            .onSizeChanged { containerW = with(density) { it.width.toDp() } }
            .focusGroup()
    ) {
        // Сколько целых карточек помещается в ширину при заданном левом паддинге
        val fullCell = itemWidth + itemSpacing
        val perPage = remember(containerW, fullCell, leftPadding, minRightPadding) {
            val usable = (containerW - leftPadding - minRightPadding).coerceAtLeast(0.dp)
            max(1, floor((usable / fullCell).toFloat()).toInt())
        }

        // Итоговая ширина полосы карточек; под неё «добиваем» правый паддинг
        val pageWidth = perPage * itemWidth + (perPage - 1) * itemSpacing
        val rightPadding = remember(containerW, leftPadding, pageWidth, minRightPadding) {
            max(minRightPadding, containerW - leftPadding - pageWidth)
        }

        // Последний допустимый «левый индекс», чтобы слева всегда был полный элемент
        val lastStartIndex = (items.size - perPage).coerceAtLeast(0)

        // SNAP: после ручного скролла выровнять к ближайшему целому индексу (offset = 0)
        LaunchedEffect(state.isScrollInProgress, perPage) {
            if (!state.isScrollInProgress) {
                val cellPx = with(density) { fullCell.roundToPx() }
                val absOffsetPx =
                    state.firstVisibleItemIndex * cellPx + state.firstVisibleItemScrollOffset
                val nearestIndex = (absOffsetPx.toFloat() / cellPx)
                    .roundToInt()
                    .coerceIn(0, lastStartIndex)
                if (nearestIndex != state.firstVisibleItemIndex ||
                    state.firstVisibleItemScrollOffset != 0
                ) {
                    state.animateScrollToItem(nearestIndex, scrollOffset = 0)
                }
            }
        }

        // Кнопки и лента
        Box(Modifier.fillMaxWidth()) {
            // Влево
            val canLeft by remember {
                derivedStateOf { state.firstVisibleItemIndex > 0 || state.firstVisibleItemScrollOffset > 0 }
            }
            EdgeArrow(
                enabled = canLeft,
                painter = painterResource(Res.drawable.left),
                alignment = Alignment.CenterStart
            ) {
                val step = if (pageByWidth) perPage else 1
                val target = (state.firstVisibleItemIndex - step).coerceAtLeast(0)
                scope.launch { state.animateScrollToItem(target) }
            }

            // Вправо
            val canRight by remember {
                derivedStateOf { state.firstVisibleItemIndex < lastStartIndex }
            }
            EdgeArrow(
                enabled = canRight,
                painter = painterResource(Res.drawable.right),
                alignment = Alignment.CenterEnd
            ) {
                val step = if (pageByWidth) perPage else 1
                val target = (state.firstVisibleItemIndex + step).coerceAtMost(lastStartIndex)
                scope.launch { state.animateScrollToItem(target) }
            }

            LazyRow(
                state = state,
                contentPadding = PaddingValues(start = leftPadding, end = rightPadding),
                horizontalArrangement = Arrangement.spacedBy(itemSpacing),
                modifier = Modifier
                    .fillMaxWidth()
                    .onPreviewKeyEvent { e ->
                        if (e.type == KeyEventType.KeyUp) when (e.key) {
                            Key.DirectionRight -> {
                                val step = if (pageByWidth) perPage else 1
                                val target =
                                    (state.firstVisibleItemIndex + step).coerceAtMost(lastStartIndex)
                                scope.launch { state.animateScrollToItem(target) }
                                true
                            }

                            Key.DirectionLeft -> {
                                val step = if (pageByWidth) perPage else 1
                                val target = (state.firstVisibleItemIndex - step).coerceAtLeast(0)
                                scope.launch { state.animateScrollToItem(target) }
                                true
                            }

                            else -> false
                        } else false
                    }
            ) {
                itemsIndexed(items) { index, item ->
                    val fr = remember { FocusRequester() }
                    var focused by remember { mutableStateOf(false) }

                    Box(
                        Modifier
                            .size(itemWidth, itemHeight)
                            .focusRequester(fr)
                            .onFocusChanged { focused = it.isFocused }
                            .focusable()
                            .pointerInput(Unit) { detectTapGestures { onItemClick(index, item) } }
                    ) { itemContent(index, item, focused) }
                }
            }
        }
    }
}

@Composable
private fun EdgeArrow(
    enabled: Boolean,
    painter: Painter,
    alignment: Alignment,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {

    Image(
        modifier = modifier
            .width(40.dp)
            .height(IntrinsicSize.Max)
            .padding(horizontal = 8.dp)
            .pointerInput(enabled) { if (enabled) detectTapGestures { onClick() } },
        painter = painter, //,
        colorFilter = ColorFilter.tint(UiKitTheme.colors.color_7),
        contentDescription = null,
    )
}

enum class ScrollStep { PerItem, PerPage }
enum class FitStrategy {
    FixedSpacing,           // используем заданный itemSpacing, правый край добиваем паддингом
    DistributeExtraSpace    // распределяем лишнее место между промежутками (как pager)
}

@Composable
fun <T> LeftAlignedCarouselRowV2(
    items: List<T>,
    modifier: Modifier = Modifier,
    itemWidth: Dp,
    itemHeight: Dp,
    itemSpacing: Dp = 16.dp,
    leftPadding: Dp = 24.dp,
    minRightPadding: Dp = 24.dp,
    scrollStep: ScrollStep = ScrollStep.PerPage,
    fit: FitStrategy = FitStrategy.DistributeExtraSpace,
    onItemClick: (index: Int, item: T) -> Unit = { _, _ -> },
    itemContent: @Composable (index: Int, item: T, isFocused: Boolean) -> Unit,
) {
    if (items.isEmpty()) return

    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()
    val density = LocalDensity.current
    var containerW by remember { mutableStateOf(0.dp) }

    BoxWithConstraints(
        modifier
            .fillMaxWidth()
            .onSizeChanged { containerW = with(density) { it.width.toDp() } }
            .focusGroup()
    ) {
        val available = (containerW - leftPadding - minRightPadding).coerceAtLeast(0.dp)

        // Сколько карточек поместится при базовом spacing
        val perPage = remember(available, itemWidth, itemSpacing) {
            max(
                1,
                floor(((available + itemSpacing) / (itemWidth + itemSpacing)).toFloat()).toInt()
            )
        }

        // Рассчитываем spacing и правый паддинг по стратегии fit
        val (effectiveSpacing, rightPadding) = remember(
            fit, available, perPage, itemWidth, itemSpacing, minRightPadding
        ) {
            if (fit == FitStrategy.DistributeExtraSpace) {
                // распределяем лишнее место между промежутками
                if (perPage > 1) {
                    val gaps = perPage - 1
                    val neededWidth = perPage * itemWidth
                    val extra = (available - neededWidth).coerceAtLeast(0.dp)
                    val spaced = max(itemSpacing, extra / gaps) // не меньше базового
                    spaced to minRightPadding // правый паддинг можно оставить минимальным
                } else {
                    // одна карточка: центруем правым паддингом
                    itemSpacing to max(minRightPadding, available - itemWidth)
                }
            } else {
                // фиксированный spacing, остаток — в правый паддинг
                val pageWidth = perPage * itemWidth + (perPage - 1) * itemSpacing
                val padRight = max(minRightPadding, available - (pageWidth - 0.dp))
                itemSpacing to padRight
            }
        }

        // Последний допустимый старт, чтобы слева всегда был полный элемент
        val lastStartIndex = (items.size - perPage).coerceAtLeast(0)

        // SNAP: всегда выравниваем первый видимый к левому краю (offset = 0),
        // не уходим правее последней «полной» позиции.
        LaunchedEffect(listState.isScrollInProgress, perPage, effectiveSpacing) {
            if (!listState.isScrollInProgress) {
                val cellPx = with(density) { (itemWidth + effectiveSpacing).roundToPx() }
                val absPx = listState.firstVisibleItemIndex * cellPx +
                        listState.firstVisibleItemScrollOffset
                val nearestIndex = (absPx.toFloat() / cellPx).roundToInt()
                    .coerceIn(0, lastStartIndex)
                if (nearestIndex != listState.firstVisibleItemIndex ||
                    listState.firstVisibleItemScrollOffset != 0
                ) {
                    listState.animateScrollToItem(nearestIndex, 0)
                }
            }
        }

        // шаг прокрутки
        val stepCount = if (scrollStep == ScrollStep.PerPage) perPage else 1

        Box(Modifier.fillMaxWidth()) {

            LazyRow(
                state = listState,
                contentPadding = PaddingValues(start = leftPadding, end = rightPadding),
                horizontalArrangement = Arrangement.spacedBy(effectiveSpacing),
                modifier = Modifier
                    .fillMaxWidth()
                    .onPreviewKeyEvent { e ->
                        if (e.type == KeyEventType.KeyUp) when (e.key) {
                            Key.DirectionRight -> {
                                val target = (listState.firstVisibleItemIndex + stepCount)
                                    .coerceAtMost(lastStartIndex)
                                scope.launch { listState.animateScrollToItem(target) }
                                true
                            }

                            Key.DirectionLeft -> {
                                val target = (listState.firstVisibleItemIndex - stepCount)
                                    .coerceAtLeast(0)
                                scope.launch { listState.animateScrollToItem(target) }
                                true
                            }

                            else -> false
                        } else false
                    }
            ) {
                itemsIndexed(items) { index, item ->
                    val fr = remember { FocusRequester() }
                    var focused by remember { mutableStateOf(false) }

                    Box(
                        Modifier
                            .size(itemWidth, itemHeight)
                            .focusRequester(fr)
                            .onFocusChanged { focused = it.isFocused }
                            .focusable()
                            .pointerInput(Unit) { detectTapGestures { onItemClick(index, item) } }
                    ) { itemContent(index, item, focused) }
                }
            }

            // левая стрелка
            val canLeft by remember {
                derivedStateOf {
                    listState.firstVisibleItemIndex > 0 ||
                            listState.firstVisibleItemScrollOffset > 0
                }
            }
            EdgeArrow(
                modifier = Modifier.align(Alignment.CenterStart),
                enabled = canLeft,
                painter = painterResource(Res.drawable.left),
                alignment = Alignment.CenterStart
            ) {
                val target = (listState.firstVisibleItemIndex - stepCount).coerceAtLeast(0)
                scope.launch { listState.animateScrollToItem(target) }
            }

            // правая стрелка
            val canRight by remember {
                derivedStateOf { listState.firstVisibleItemIndex < lastStartIndex }
            }
            EdgeArrow(
                modifier = Modifier.align(Alignment.CenterEnd),
                enabled = canRight,
                painter = painterResource(Res.drawable.right),
                alignment = Alignment.CenterEnd
            ) {
                val target =
                    (listState.firstVisibleItemIndex + stepCount).coerceAtMost(lastStartIndex)
                scope.launch { listState.animateScrollToItem(target) }
            }
        }
    }
}

@Composable
fun <T> LeftAlignedCarouselRowV3(
    items: List<T>,
    modifier: Modifier = Modifier,
    itemWidth: Dp,
    itemHeight: Dp,
    baseItemSpacing: Dp = 20.dp,
    leftPadding: Dp = 24.dp,
    minRightPadding: Dp = 24.dp,
    scrollStep: ScrollStep = ScrollStep.PerPage,
    fit: FitStrategy = FitStrategy.DistributeExtraSpace,
    onItemClick: (index: Int, item: T) -> Unit = { _, _ -> },
    leftContent: @Composable (enabled: Boolean, onClick: () -> Unit) -> Unit,
    rightContent: @Composable (enabled: Boolean, onClick: () -> Unit) -> Unit,
    itemContent: @Composable (index: Int, item: T, isFocused: Boolean) -> Unit,
) {
    if (items.isEmpty()) return

    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()
    val density = LocalDensity.current

    // >>> ХОСТИМ значения, которые вычислит центр
    var perPage by remember { mutableStateOf(1) }
    var lastStartIndex by remember { mutableStateOf(0) }

    Row(
        modifier
            .fillMaxWidth()
            .focusGroup(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // ----- левая кнопка -----
        val canLeft by remember {
            derivedStateOf { listState.canScrollBackward }
        }
        leftContent(canLeft) {
            val step = if (scrollStep == ScrollStep.PerPage) perPage else 1
            val target = (listState.firstVisibleItemIndex - step).coerceAtLeast(0)
            scope.launch { listState.animateScrollToItem(target) }
        }

        // ----- ЦЕНТР: считает perPage/spacing/lastStart и рисует ленту -----
        BoxWithConstraints(
            Modifier
                .weight(1f)
                .requiredHeight(itemHeight)   // фиксируем высоту центра
        ) {
            // доступная ширина только центральной области
            val available = (maxWidth - leftPadding - minRightPadding).coerceAtLeast(0.dp)

            // ровно сколько карточек влезает при базовом spacing
            val computedPerPage = max(
                1,
                floor(((available + baseItemSpacing) / (itemWidth + baseItemSpacing)).toFloat()).toInt()
            )

            // spacing + правый паддинг по стратегии
            val (effectiveSpacing, rightPadding) =
                if (fit == FitStrategy.DistributeExtraSpace) {
                    if (computedPerPage > 1) {
                        val gaps = computedPerPage - 1
                        val needed = computedPerPage * itemWidth
                        val extra = (available - needed).coerceAtLeast(0.dp)
                        max(baseItemSpacing, extra / gaps) to minRightPadding
                    } else {
                        baseItemSpacing to max(minRightPadding, available - itemWidth)
                    }
                } else {
                    val pageW = computedPerPage * itemWidth + (computedPerPage - 1) * baseItemSpacing
                    baseItemSpacing to max(minRightPadding, available - pageW)
                }

            // последний допустимый старт, чтобы слева всегда полный
            val computedLastStart = (items.size - computedPerPage).coerceAtLeast(0)

            // Синхронизируем метрики сразу после композиции (без кадра задержки LaunchedEffect)
            SideEffect {
                perPage = computedPerPage
                lastStartIndex = computedLastStart
            }

            // SNAP к полному элементу слева
            LaunchedEffect(listState.isScrollInProgress, computedPerPage, effectiveSpacing) {
                if (!listState.isScrollInProgress) {
                    val cellPx = with(density) { (itemWidth + effectiveSpacing).roundToPx() }
                    val absPx = listState.firstVisibleItemIndex * cellPx +
                            listState.firstVisibleItemScrollOffset
                    val nearestIndex = (absPx.toFloat() / cellPx)
                        .roundToInt()
                        .coerceIn(0, computedLastStart)
                    if (nearestIndex != listState.firstVisibleItemIndex ||
                        listState.firstVisibleItemScrollOffset != 0
                    ) {
                        listState.animateScrollToItem(nearestIndex, 0)
                    }
                }
            }

            LazyRow(
                state = listState,
                contentPadding = PaddingValues(start = leftPadding, end = rightPadding),
                horizontalArrangement = Arrangement.spacedBy(effectiveSpacing),
                modifier = Modifier
                    .fillMaxWidth()
                    .wrapContentHeight(Alignment.CenterVertically) // не сжимать; центрировать по высоте
            ) {
                itemsIndexed(items) { index, item ->
                    var focused by remember { mutableStateOf(false) }
                    Box(
                        Modifier
                            .requiredSize(itemWidth, itemHeight)  // фиксированный размер карточки
                            .onFocusChanged { focused = it.isFocused }
                            .focusable()
                            .pointerInput(Unit) { detectTapGestures { onItemClick(index, item) } }
                    ) { itemContent(index, item, focused) }
                }
            }
        }

        // ----- правая кнопка: canScrollForward точнее, чем сравнение с lastStartIndex -----
        val canRight by remember {
            derivedStateOf { listState.canScrollForward }
        }
        rightContent(canRight) {
            val step = if (scrollStep == ScrollStep.PerPage) perPage else 1
            val target = (listState.firstVisibleItemIndex + step).coerceAtMost(lastStartIndex)
            scope.launch { listState.animateScrollToItem(target) }
        }
    }
}

// Вспомогательный пересчёт perPage
private fun calcPerPage(
    containerW: Dp,
    itemWidth: Dp,
    baseSpacing: Dp,
    leftPadding: Dp,
    minRightPadding: Dp,
    fit: FitStrategy
): Pair<Int, Dp> {
    val available = (containerW - leftPadding - minRightPadding).coerceAtLeast(0.dp)
    val perPage = max(
        1,
        floor(((available + baseSpacing) / (itemWidth + baseSpacing)).toFloat()).toInt()
    )
    return perPage to available
}
